import { useState } from 'react';
import { getUserProfile } from '../../utils/storage';
import { formatDistance } from '../../utils/units';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1910 + 1 }, (_, i) => currentYear - i);

export default function RepairVehicleSelect({ data, update, onNext, onBack, vehicles }) {
  const [mode, setMode] = useState(vehicles?.length > 0 ? 'garage' : 'manual'); // 'garage' | 'manual'
  const profile = getUserProfile();

  function selectVehicle(v) {
    update({
      repairVehicleYear: String(v.year),
      repairVehicleMake: v.make,
      repairVehicleModel: v.model,
      repairVehicleId: v.id,
      repairMileage: v.mileage || '',
      repairZipCode: data.repairZipCode || profile?.zipCode || '',
    });
  }

  const canProceed = !!(data.repairVehicleYear && data.repairVehicleMake && data.repairVehicleModel);

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">Vehicle Info</h1>
        <p className="screen-subtitle">
          Which vehicle is this repair quote for?
        </p>
      </div>

      {/* Mode switcher (only show if vehicles exist) */}
      {vehicles?.length > 0 && (
        <div style={{
          display: 'flex',
          gap: 0,
          borderRadius: 100,
          overflow: 'hidden',
          border: '1.5px solid var(--border)',
          marginBottom: 20,
        }}>
          {['garage', 'manual'].map((t) => (
            <button
              key={t}
              onClick={() => setMode(t)}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '0.8125rem',
                fontWeight: 600,
                background: mode === t ? 'var(--accent)' : 'transparent',
                color: mode === t ? 'white' : 'var(--text-muted)',
                transition: 'all 0.15s',
              }}
            >
              {t === 'garage' ? 'From Garage' : 'Enter Manually'}
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, paddingTop: 8 }}>
        {mode === 'garage' && vehicles?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {vehicles.map((v) => {
              const isSelected = data.repairVehicleId === v.id;
              const label = `${v.year} ${v.make} ${v.model}`;
              return (
                <button
                  key={v.id}
                  className="card"
                  onClick={() => selectVehicle(v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderColor: isSelected ? 'var(--accent)' : undefined,
                    background: isSelected ? 'var(--accent-subtle)' : undefined,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-sm)',
                    background: v.photo ? `url(${v.photo}) center/cover no-repeat` : 'var(--bg-elevated)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}>
                    {!v.photo && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 17h14M5 17a2 2 0 01-2-2V9a1 1 0 011-1h1l2-4h10l2 4h1a1 1 0 011 1v6a2 2 0 01-2 2M5 17a2 2 0 002 2h10a2 2 0 002-2" />
                        <circle cx="7.5" cy="17" r="1.5" />
                        <circle cx="16.5" cy="17" r="1.5" />
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: isSelected ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {label}
                    </div>
                    {v.mileage && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {formatDistance(v.mileage, v.distance_units)}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {mode === 'manual' && (
          <>
            <div className="form-group">
              <label className="form-label">Year</label>
              <select
                className="form-select"
                value={data.repairVehicleYear || ''}
                onChange={(e) => update({ repairVehicleYear: e.target.value, repairVehicleId: null })}
              >
                <option value="">Year</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Make</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Toyota, Honda, BMW"
                value={data.repairVehicleMake || ''}
                onChange={(e) => update({ repairVehicleMake: e.target.value, repairVehicleId: null })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Model</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Camry, Civic, 3 Series"
                value={data.repairVehicleModel || ''}
                onChange={(e) => update({ repairVehicleModel: e.target.value, repairVehicleId: null })}
              />
            </div>
          </>
        )}

        {/* ZIP code + mileage row */}
        <div style={{ display: 'flex', gap: 12, marginTop: mode === 'garage' ? 16 : 0 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">ZIP Code</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              maxLength={5}
              placeholder="e.g. 90210"
              value={data.repairZipCode || profile?.zipCode || ''}
              onChange={(e) => update({ repairZipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Mileage</label>
            <input
              className="form-input"
              type="number"
              inputMode="numeric"
              placeholder="e.g. 85000"
              value={data.repairMileage || ''}
              onChange={(e) => update({ repairMileage: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Analyze Quote
        </button>
      </div>
    </>
  );
}
